import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { UploadIcon, type IconComponent } from '../icons';
import './FileDrop.css';

/** Receipt media accepted everywhere; never image/heic (Safari would then pick HEIC). */
export const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export interface FileDropProps {
  onFiles: (files: File[]) => void;
  /** `accept` attribute; default receipt media. */
  accept?: string;
  multiple?: boolean;
  /** Main sentence: "Dra hit en bild eller PDF". */
  label?: ReactNode;
  /** Secondary: "eller välj från datorn". */
  hint?: ReactNode;
  /** Smaller inline variant for cards. */
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  /** `capture="environment"` for phones. */
  capture?: 'environment' | 'user';
  /** Glyph in the zone; default the upload arrow. A camera zone says camera. */
  icon?: IconComponent;
}

/** Filter a DataTransfer/FileList down to accepted files. */
export function acceptedFiles(list: Iterable<File>, accept: string = RECEIPT_ACCEPT): File[] {
  const rules = accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  const out: File[] = [];
  for (const f of list) {
    const type = f.type.toLowerCase();
    const ext = f.name.includes('.') ? `.${f.name.split('.').pop()!.toLowerCase()}` : '';
    const ok = rules.some((r) => {
      if (r.startsWith('.')) return ext === r;
      if (r.endsWith('/*')) return type.startsWith(r.slice(0, -1));
      return type === r;
    });
    if (ok) out.push(f);
  }
  return out;
}

/** Drop zone + file picker. The whole zone is a button; keyboard opens the picker. */
export function FileDrop({ onFiles, accept = RECEIPT_ACCEPT, multiple = true, label, hint, compact, disabled, className, capture, icon: Icon = UploadIcon }: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const id = useId();

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const files = acceptedFiles(Array.from(e.dataTransfer.files), accept);
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };

  return (
    <div
      className={['file-drop', compact ? 'file-drop-compact' : '', over ? 'file-drop-over' : '', disabled ? 'file-drop-disabled' : '', className ?? ''].filter(Boolean).join(' ')}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        capture={capture}
        disabled={disabled}
        className="visually-hidden"
        onChange={(e) => {
          const files = acceptedFiles(Array.from(e.target.files ?? []), accept);
          if (files.length) onFiles(files);
          e.target.value = '';
        }}
      />
      <label htmlFor={id} className="file-drop-label">
        <span className="file-drop-icon">
          <Icon size={compact ? 20 : 24} />
        </span>
        <span className="file-drop-text">
          <span className="file-drop-main">{label ?? 'Dra hit en bild eller PDF'}</span>
          <span className="file-drop-hint">{hint ?? 'eller klicka för att välja från datorn'}</span>
        </span>
      </label>
    </div>
  );
}
